/** G6 通道与降级：`GET /channels`、`GET /degradations` 的解析与取数（对接文档 §3.11）。
 *
 * 形状以后端 `someo_keel/api/console_g6.py` 为准；缺字段抛 ContractError，不猜、不降级成空表。
 */

import { useQuery } from "@tanstack/react-query";

import {
  apiUrl,
  expectList,
  expectRecord,
  optionalString,
  query,
  requireBoolean,
  requireString,
} from "../api/client";
import { ContractError, isRetryable } from "../api/errors";
import type { ReadResult } from "../api/queries";

export type ChannelStatus = "ok" | "failed" | "not_wired" | "unknown";

export interface ChannelRecord {
  channel_id: string;
  label: string;
  kind: string;
  adapter: string;
  venue: string;
  active: boolean;
  status: ChannelStatus;
  status_detail: string;
  last_check_utc: string | null;
  last_check_ok: boolean | null;
  last_event_id: string | null;
  last_event_utc: string | null;
}

export interface ChannelsPayload {
  as_of_utc: string;
  active_channel_id: string;
  channels: ChannelRecord[];
}

export interface DegradationRecord {
  id: string;
  ts_utc: string;
  ended_utc: string | null;
  kind: string;
  nature: string;
  environment: string | null;
  from_channel_id: string | null;
  to_channel_id: string | null;
  trigger: string | null;
  operator: string | null;
  reason: string | null;
  result: string;
  alert_id: string | null;
  reconciliation_run_id: string | null;
  balanced_after: boolean | null;
}

const URL_CHANNELS = apiUrl("/channels");
const URL_DEGRADATIONS = apiUrl("/degradations");
const STATUSES: readonly string[] = ["ok", "failed", "not_wired", "unknown"];

function optionalBool(source: Record<string, unknown>, key: string, url: string): boolean | null {
  const value = source[key];
  if (value === undefined || value === null) return null;
  if (typeof value !== "boolean") throw new ContractError(`字段 ${key} 应为布尔或 null`, url, source);
  return value;
}

export function parseChannels(body: unknown): ChannelsPayload {
  const source = expectRecord(body, URL_CHANNELS, "channels 响应");
  const channels = expectList(source["channels"], "channels", URL_CHANNELS, "通道").map((item) => {
    const row = expectRecord(item, URL_CHANNELS, "通道记录");
    const status = requireString(row, "status", URL_CHANNELS);
    if (!STATUSES.includes(status)) {
      throw new ContractError(`status 取值 ${status} 不在 ok|failed|not_wired|unknown 内`, URL_CHANNELS, row);
    }
    return {
      channel_id: requireString(row, "channel_id", URL_CHANNELS),
      label: requireString(row, "label", URL_CHANNELS),
      kind: requireString(row, "kind", URL_CHANNELS),
      adapter: requireString(row, "adapter", URL_CHANNELS),
      venue: requireString(row, "venue", URL_CHANNELS),
      active: requireBoolean(row, "active", URL_CHANNELS),
      status: status as ChannelStatus,
      status_detail: requireString(row, "status_detail", URL_CHANNELS),
      last_check_utc: optionalString(row, "last_check_utc"),
      last_check_ok: optionalBool(row, "last_check_ok", URL_CHANNELS),
      last_event_id: optionalString(row, "last_event_id"),
      last_event_utc: optionalString(row, "last_event_utc"),
    } satisfies ChannelRecord;
  });
  return {
    as_of_utc: requireString(source, "as_of_utc", URL_CHANNELS),
    active_channel_id: requireString(source, "active_channel_id", URL_CHANNELS),
    channels,
  };
}

export function parseDegradations(body: unknown): DegradationRecord[] {
  if (!Array.isArray(body)) throw new ContractError("degradations 响应应为数组", URL_DEGRADATIONS, body);
  return body.map((item) => {
    const row = expectRecord(item, URL_DEGRADATIONS, "降级记录");
    return {
      id: requireString(row, "id", URL_DEGRADATIONS),
      ts_utc: requireString(row, "ts_utc", URL_DEGRADATIONS),
      ended_utc: optionalString(row, "ended_utc"),
      kind: requireString(row, "kind", URL_DEGRADATIONS),
      nature: requireString(row, "nature", URL_DEGRADATIONS),
      environment: optionalString(row, "environment"),
      from_channel_id: optionalString(row, "from_channel_id"),
      to_channel_id: optionalString(row, "to_channel_id"),
      trigger: optionalString(row, "trigger"),
      operator: optionalString(row, "operator"),
      reason: optionalString(row, "reason"),
      result: requireString(row, "result", URL_DEGRADATIONS),
      alert_id: optionalString(row, "alert_id"),
      reconciliation_run_id: optionalString(row, "reconciliation_run_id"),
      balanced_after: optionalBool(row, "balanced_after", URL_DEGRADATIONS),
    } satisfies DegradationRecord;
  });
}

export const readChannels = (): Promise<ChannelsPayload> => query<unknown>("/channels").then(parseChannels);
export const readDegradations = (): Promise<DegradationRecord[]> =>
  query<unknown>("/degradations").then(parseDegradations);

const READ = {
  retry: (count: number, error: unknown) => count < 2 && isRetryable(error),
  retryDelay: 1_500,
} as const;

export function useChannels(): ReadResult<ChannelsPayload> {
  return useQuery<ChannelsPayload, Error>({ queryKey: ["channels"], queryFn: readChannels, ...READ });
}

export function useDegradations(): ReadResult<DegradationRecord[]> {
  return useQuery<DegradationRecord[], Error>({ queryKey: ["degradations"], queryFn: readDegradations, ...READ });
}

/** 切换演练页头文案：无演练记录用中性「未做」，不写 0 次「通过」。 */
export function rehearsalSummary(rows: DegradationRecord[]): string {
  const n = rows.filter((r) => r.nature === "rehearsal").length;
  return n === 0 ? "切换演练：未做（broker-portability「切换演练」要求纸上盘至少一次）" : `切换演练：已做 ${n} 次`;
}
